import React, { useEffect, useState } from 'react';
import { Dialog, DialogFooter, DialogType, getTheme, PrimaryButton, Text } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import type { IPresencePrompt } from './inactivityStorage';

export interface IPresenceConfirmModalProps {
    prompt: IPresencePrompt | null;
    manualOooSignsOut: boolean;
    onConfirm: () => void;
    onTimeout: () => void;
}

function formatCountdown(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function promptText(prompt: IPresencePrompt, manualOooSignsOut: boolean): string {
    switch (prompt.rule) {
        case 'idle-chain':
            return IvicosStrings.presencePromptIdleChainText;
        case 'manual-ooo':
            return manualOooSignsOut ? IvicosStrings.presencePromptManualOooLogoutText : IvicosStrings.presencePromptManualOooTodayText;
        case 'midnight':
            return IvicosStrings.presencePromptMidnightText;
    }
}

/** Blocking "Are you still there?" dialog. Only the button confirms; the countdown is derived from the shared deadline. */
const PresenceConfirmModal: React.FC<IPresenceConfirmModalProps> = ({ prompt, manualOooSignsOut, onConfirm, onTimeout }) => {
    const theme = getTheme();
    const [remainingSeconds, setRemainingSeconds] = useState(0);

    useEffect(() => {
        if (!prompt) return;
        const update = () => {
            const seconds = Math.max(0, Math.ceil((prompt.deadline - Date.now()) / 1000));
            setRemainingSeconds(seconds);
            if (seconds === 0) {
                clearInterval(id);
                onTimeout();
            }
        };
        const id = setInterval(update, 1000);
        update();
        return () => clearInterval(id);
    }, [prompt, onTimeout]);

    if (!prompt) return null;

    return (
        <Dialog
            hidden={false}
            dialogContentProps={{ type: DialogType.normal, title: IvicosStrings.presencePromptTitle, showCloseButton: false }}
            modalProps={{ isBlocking: true, styles: { main: { maxWidth: 450 } } }}
        >
            <div aria-label="presence-confirm-modal">
                <Text variant="medium" block>
                    {promptText(prompt, manualOooSignsOut)}
                </Text>
                <Text variant="large" block style={{ fontWeight: 600, marginTop: 8 }} aria-label="presence-confirm-countdown">
                    {formatCountdown(remainingSeconds)}
                </Text>
            </div>
            <DialogFooter>
                <PrimaryButton
                    aria-label="presence-confirm-button"
                    onClick={onConfirm}
                    text={IvicosStrings.presencePromptButton}
                    styles={{ root: { backgroundColor: theme.palette.themePrimary, borderColor: theme.palette.themePrimary } }}
                />
            </DialogFooter>
        </Dialog>
    );
};

export default PresenceConfirmModal;
